/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";

import { sanitizeText } from "@/utils/string";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document } from "@contentful/rich-text-types";
import { Accordion, AccordionDetails, AccordionSummary, Box, Fade, Stack, Typography } from "@mui/material";
import { MinusCircle, PlusCircle } from "@phosphor-icons/react";

import { AccordionCustomMediaPlayer } from "./AccordionCustomMediaPlayer";
import { AccordionCustomThumb } from "./AccordionCustomThumb";
import { styles } from "./styles";
import { AccordionCustomProps } from "./types";

export const AccordionCustom: React.FC<AccordionCustomProps> = ({
  question,
  answer,
  accordionColor = "#0068FF",
  questionColor = "#0045B5",
  answerColor = "#58595b",
  createdAt,
  answerMedia,
  answerMediaType,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false);

  const trackEvent = (isExpanded: boolean) => {
    if (isExpanded) {
      try {
        const sanitizedQuestion = sanitizeText(question);
        if ((window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "hero_interaction",
            content_type: `${sanitizedQuestion}`
          });
          console.log("Evento de interação enviado para o Google Tag Manager:", `${sanitizedQuestion}`);
        }
      } catch (error) {
        console.error("Erro ao enviar o evento para o Google Tag Manager", error);
      }
    }
  };

  const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    trackEvent(isExpanded);
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange} disableGutters css={styles.accordion} {...props}>
      <AccordionSummary
        expandIcon={!expanded ? <PlusCircle size={32} color={accordionColor} /> : <MinusCircle size={32} color={accordionColor} />}
        css={styles.accordion__summary}
      >
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="center">
              <Typography fontSize="20px" fontWeight={600} color={questionColor} mr={2}>
                {question}
              </Typography>

              {answerMedia && !expanded ? (
                <Box display={{ xs: "none", md: "inherit" }}>
                  <AccordionCustomThumb mediaSrc={answerMedia} mediaType={answerMediaType} />
                </Box>
              ) : null}
            </Stack>

            {createdAt && !expanded ? (
              <Typography
                fontSize={16}
                color={questionColor}
                fontWeight={400}
                whiteSpace="nowrap"
                alignSelf={{
                  xs: "flex-start",
                  md: "center"
                }}
                mr="8px"
              >
                Publicação: {createdAt}
              </Typography>
            ) : null}
          </Stack>

          {answerMedia && !expanded ? (
            <Stack display={{ xs: "inherit", md: "none" }} justifyContent="center">
              <AccordionCustomThumb mediaSrc={answerMedia} mediaType={answerMediaType} />
            </Stack>
          ) : null}
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <Stack ml={6} mr={{ xs: "inherit", md: answerMedia ? "322px" : "inherit" }} mb={{ xs: answerMedia ? "190px" : "inherit", md: "inherit" }}>
          <Typography component="div" fontSize={{ xs: 14, sm: 16, md: 16 }} color={answerColor} css={styles.acortion__answer}>
            {typeof answer === "object" ? documentToReactComponents(answer as Document) : answer}
          </Typography>

          {createdAt && expanded ? (
            <Typography fontSize={16} lineHeight="24px" color={questionColor} fontWeight={400} mt={{ xs: "8px", md: "48px" }}>
              Publicação: {createdAt}
            </Typography>
          ) : null}
        </Stack>

        <Fade in={Boolean(answerMedia && expanded)} timeout={1000}>
          <Box>{answerMedia && expanded ? <AccordionCustomMediaPlayer src={answerMedia} type={answerMediaType} /> : null}</Box>
        </Fade>
      </AccordionDetails>
    </Accordion>
  );
};
